import {
  AlertCircle,
  Building2,
  ChevronDown,
  ChevronRight,
  Network,
  Plus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import type { Division, Team } from '../services/api';
import type { OrganizationSnapshot } from './model';
import { groupRelationshipIssue, type EntitySelection } from './graph';

export default function StructureOutline({
  snapshot,
  selection,
  onSelect,
  canEdit,
}: {
  snapshot: OrganizationSnapshot;
  selection: EntitySelection | null;
  onSelect: (selection: EntitySelection) => void;
  canEdit: boolean;
}) {
  const [collapsed, setCollapsed] = useState(new Set<string>());
  const toggle = (id: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const seen = new Set<string>();
  const team = (unit: Team, depth: number): React.ReactNode => {
    if (seen.has(unit.id)) return null;
    seen.add(unit.id);
    const children = snapshot.teams.filter((t) => t.parentTeamId === unit.id);
    const issue = groupRelationshipIssue(snapshot, { kind: 'team', id: unit.id });
    const expanded = !collapsed.has(unit.id);
    const members = snapshot.teamMemberships.filter((m) => m.teamId === unit.id).length;
    return (
      <div key={unit.id} className="org-tree-node">
        <div
          className={`org-tree-row ${selection?.kind === 'team' && selection.id === unit.id ? 'is-selected' : ''}`}
          style={{ paddingLeft: `${14 + depth * 14}px` }}
        >
          {children.length ? (
            <button
              type="button"
              className="org-tree-toggle"
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${unit.name}`}
              aria-expanded={expanded}
              onClick={() => toggle(unit.id)}
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : (
            <span className="org-tree-spacer" />
          )}
          <button
            type="button"
            className="org-tree-select"
            onClick={() => onSelect({ kind: 'team', id: unit.id })}
          >
            <Users size={14} />
            <span>{unit.name}</span>
            {issue && <AlertCircle size={13} aria-label={issue} className="org-role-issue" />}
            <small>{members}</small>
          </button>
        </div>
        {expanded && children.map((t) => team(t, depth + 1))}
      </div>
    );
  };
  const division = (unit: Division, depth: number): React.ReactNode => {
    if (seen.has(unit.id)) return null;
    seen.add(unit.id);
    const children = snapshot.divisions.filter((d) => d.parentDivisionId === unit.id);
    const teams = snapshot.teams.filter(
      (t) =>
        t.divisionId === unit.id &&
        (!t.parentTeamId || !snapshot.teams.some((p) => p.id === t.parentTeamId))
    );
    const issue = groupRelationshipIssue(snapshot, { kind: 'division', id: unit.id });
    const expanded = !collapsed.has(unit.id);
    return (
      <div key={unit.id} className="org-tree-node">
        <div
          className={`org-tree-row ${selection?.kind === 'division' && selection.id === unit.id ? 'is-selected' : ''}`}
          style={{ paddingLeft: `${14 + depth * 14}px` }}
        >
          {children.length || teams.length ? (
            <button
              type="button"
              className="org-tree-toggle"
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${unit.name}`}
              aria-expanded={expanded}
              onClick={() => toggle(unit.id)}
            >
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : (
            <span className="org-tree-spacer" />
          )}
          <button
            type="button"
            className="org-tree-select"
            onClick={() => onSelect({ kind: 'division', id: unit.id })}
          >
            <Building2 size={14} />
            <span>{unit.name}</span>
            {issue && <AlertCircle size={13} aria-label={issue} className="org-role-issue" />}
            <small>{snapshot.positions.filter((p) => p.divisionId === unit.id).length}</small>
          </button>
        </div>
        {expanded && (
          <>
            {children.map((d) => division(d, depth + 1))}
            {teams.map((t) => team(t, depth + 1))}
          </>
        )}
      </div>
    );
  };
  const divisionRoots = snapshot.divisions.filter(
    (d) => !d.parentDivisionId || !snapshot.divisions.some((p) => p.id === d.parentDivisionId)
  );
  const teamRoots = snapshot.teams.filter(
    (t) =>
      (!t.divisionId || !snapshot.divisions.some((d) => d.id === t.divisionId)) &&
      (!t.parentTeamId || !snapshot.teams.some((p) => p.id === t.parentTeamId))
  );
  // Mark collapsed descendants as covered; only genuinely disconnected units belong in the review branch.
  const covered = new Set<string>();
  const coverTeam = (id: string) => {
    if (covered.has(id)) return;
    covered.add(id);
    snapshot.teams.filter((t) => t.parentTeamId === id).forEach((t) => coverTeam(t.id));
  };
  const coverDivision = (id: string) => {
    if (covered.has(id)) return;
    covered.add(id);
    snapshot.divisions.filter((d) => d.parentDivisionId === id).forEach((d) => coverDivision(d.id));
    snapshot.teams
      .filter(
        (t) =>
          t.divisionId === id &&
          (!t.parentTeamId || !snapshot.teams.some((p) => p.id === t.parentTeamId))
      )
      .forEach((t) => coverTeam(t.id));
  };
  divisionRoots.forEach((d) => coverDivision(d.id));
  teamRoots.forEach((t) => coverTeam(t.id));
  return (
    <div className="org-outline-body">
      <div className="org-outline-intro">
        <span className="org-eyebrow">ORGANISATIONAL GROUPS</span>
        <p>Groups connect your people and responsibilities. Select one to focus the chart.</p>
      </div>
      <div className="org-tree" aria-label="Division and team structure">
        {divisionRoots.map((d) => division(d, 0))}
        {teamRoots.length > 0 && <div className="org-tree-section-label">Standalone teams</div>}
        {teamRoots.map((t) => team(t, 0))}
        {snapshot.divisions.some((d) => !covered.has(d.id)) ||
        snapshot.teams.some((t) => !covered.has(t.id)) ? (
          <>
            <div className="org-tree-section-label">Needs a structure review</div>
            {snapshot.divisions.filter((d) => !covered.has(d.id)).map((d) => division(d, 0))}
            {snapshot.teams.filter((t) => !covered.has(t.id)).map((t) => team(t, 0))}
          </>
        ) : null}
      </div>
      {!snapshot.divisions.length && !snapshot.teams.length && (
        <div className="org-empty">
          <Building2 size={22} />
          <p>No groups yet</p>
          <small>Create a division, or start with a standalone team.</small>
        </div>
      )}
      {canEdit && (
        <div className="org-outline-actions">
          <button
            type="button"
            className="org-button org-button-secondary"
            onClick={() => onSelect({ kind: 'division' })}
          >
            <Plus size={14} />
            Add division
          </button>
          <button
            type="button"
            className="org-button org-button-secondary"
            onClick={() => onSelect({ kind: 'team' })}
          >
            <Plus size={14} />
            Add team
          </button>
        </div>
      )}
      <div className="org-outline-key">
        <Network size={14} />
        <span>Chart lines show reporting relationships.</span>
      </div>
    </div>
  );
}
